<template>
  <div class="trip-speed-chart">
    <div class="speed-axis">
      <div class="speed-domain">
        <div>
          {{ ceiledMaxSpeed }}
          <span>{{ $t('trip.overview.chart.km_per_hour') }}</span>
        </div>
        <div>0</div>
      </div>
      <div class="steps">
        <div
          class="step"
          v-for="(item, index) in new Array(7)"
          :key="index"
          :style="{ 'width': `${36 - 4 * index}px`}"
        ></div>
      </div>
    </div>
    <div class="speed-chart">
      <svg
        :width="svgWidth + margin.left + margin.right"
        :height="height + margin.top + margin.bottom"
      >
        <g :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }">
          <path class="line" :d="pathDesc"></path>
          <line
            v-if="!hoveredItem && !timestamp"
            class="mean-line"
            :x1="x(start)"
            :x2="x(end) + margin.right"
            :y1="y(meanSpeed)"
            :y2="y(meanSpeed)"
          ></line>
          <line
            v-if="hoveredItem"
            class="hover-line"
            :x1="x(hoveredItem.timestamp)"
            :x2="x(hoveredItem.timestamp)"
            :y1="0 - margin.top"
            :y2="height"
          ></line>
          <line
            v-if="timestamp"
            class="hover-line"
            :x1="x(timestamp)"
            :x2="x(timestamp)"
            :y1="0 - margin.top"
            :y2="height"
          ></line>
          <circle
            class="hover-circle"
            v-if="hoveredItem"
            :cx="x(hoveredItem.timestamp)"
            :cy="y(hoveredItem.speed)"
            r="4"
          ></circle>
          <circle
            class="hover-circle is-small"
            v-if="timestamp"
            :cx="x(timestamp)"
            :cy="y(getWaypointByTimestamp(timestamp).speed)"
            r="2"
          ></circle>
        </g>
        <rect
          ref="overlay"
          class="overlay"
          :width="svgWidth"
          :height="height"
          :style="{ 'transform': `translate(${margin.left}px, ${margin.top}px)` }"
        ></rect>
        <defs>
          <linearGradient id="linear" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#4571D8"></stop>
            <stop offset="100%" stop-color="#6A9AE9"></stop>
          </linearGradient>
        </defs>
      </svg>
      <div
        class="mean-rect"
        v-if="!hoveredItem && !timestamp"
        :style="{ 'top': `${margin.top - 14}px`, 'left': `${margin.left}px`, 'transform': `translateY(${y(meanSpeed)}px)` }"
      >
        <div
          class="mean-text"
        >{{ $t('trip.overview.chart.avg_speed') }}: {{ $t('ui.values.km_per_hour', [roundedMeanSpeed]) }}</div>
      </div>
      <div
        class="tooltip"
        v-if="hoveredItem"
        :style="{ 'transform': `translate(${x(hoveredItem.timestamp) + margin.left}px, ${y(hoveredItem.speed) + margin.top}px)` }"
      >
        <div class="tooltip-inner">
          <div class="level">
            <div class="level-left">
              <i class="material-icons">&#xE5C8;</i>
              {{ $t('trip.overview.chart.speed') }}
            </div>
            <div
              class="level-right"
            >{{ $t('ui.values.km_per_hour', [humanizeSpeed(hoveredItem.speed)]) }}</div>
          </div>
          <div class="level">
            <div class="level-left">
              <i class="material-icons">&#xE192;</i>
              {{ $t('trip.overview.chart.time') }}
            </div>
            <div class="level-right">{{ humanizeTimestamp(hoveredItem.timestamp) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { scaleTime, scaleLinear } from 'd3-scale';
import { area, curveBasis } from 'd3-shape';
import { select, mouse } from 'd3-selection';
import { extent, bisector } from 'd3-array';
import { maxBy, meanBy } from 'lodash';
import * as moment from 'moment';

export default {
  name: 'TripSpeedChart',

  props: {
    start: {
      type: Date,
      required: true,
    },
    end: {
      type: Date,
      required: true,
    },
    waypoints: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
    timestamp: {
      type: Date,
      required: false,
    },
  },

  data() {
    return {
      svgWidth: 0,
      height: 57.2,
      margin: { top: 5, bottom: 0, left: 16, right: 24 },
      x: scaleTime(),
      y: scaleLinear(),
      area: area(),
      pathDesc: '',
      hoveredItem: null,
    };
  },

  computed: {
    maxSpeed() {
      const maxItem = maxBy(this.waypoints, 'speed');

      return maxItem.speed;
    },

    ceiledMaxSpeed() {
      return Math.ceil(this.maxSpeed);
    },

    meanSpeed() {
      return meanBy(this.waypoints, 'speed');
    },

    roundedMeanSpeed() {
      return Math.round(this.meanSpeed);
    },
  },

  methods: {
    humanizeSpeed(speed) {
      return Math.round(speed);
    },

    humanizeTimestamp(timestamp) {
      return moment(timestamp).format('h:mm a');
    },

    getWaypointByTimestamp(timestamp) {
      const bisectDate = bisector(d => d.timestamp).left;
      const i = bisectDate(this.waypoints, timestamp);
      const d = this.waypoints[i];

      return d;
    },
  },

  mounted() {
    this.svgWidth = this.width - this.margin.left - this.margin.right - 144;
    this.x.domain([this.start, this.end]).range([0, this.svgWidth]);

    this.y.domain([0, this.maxSpeed]).range([this.height, 0]);

    this.area
      .x(d => this.x(d.timestamp))
      .y0(this.y(0))
      .y1(d => this.y(d.speed))
      .curve(curveBasis);

    this.pathDesc = this.area(this.waypoints);

    select(this.$refs.overlay)
      .on('mouseout', () => {
        this.hoveredItem = null;
        this.$emit('hovered-speed-chart', null);
      })
      .on('mousemove', () => {
        const bisectDate = bisector(d => d.timestamp).left;
        const x0 = this.x.invert(mouse(this.$refs.overlay)[0]);
        const i = bisectDate(this.waypoints, x0, 1);
        const d0 = this.waypoints[i - 1];
        const d1 = this.waypoints[i];
        let d;

        if (d1) {
          d = x0 - d0.timestamp > d1.timestamp - x0 ? d1 : d0;
        } else {
          d = d0;
        }

        this.hoveredItem = d;
        this.$emit('hovered-speed-chart', d);
      });
  },

  watch: {
    width: function(newWidth) {
      if (newWidth === 0) {
        return;
      }

      this.svgWidth = this.width - this.margin.left - this.margin.right - 144;
      this.x.range([0, this.svgWidth]);
      this.pathDesc = this.area(this.waypoints);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.trip-speed-chart {
  display: flex;
  height: 62px;
  border-bottom: 1px solid rgba($black, 0.24);
}

.speed-axis {
  position: relative;
  width: 144px;
  padding: 0.5rem 1rem;
  background-color: #2f2f2f;
  background: linear-gradient(#2f2f2f, #434343);

  .steps {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    top: 0.75rem;
    right: 1rem;
  }

  .step {
    height: 2px;
    background-color: rgba($white, 0.2);
    border-radius: 2px;
    margin-bottom: 0.25rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.speed-domain {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  font-size: 0.625rem;
  line-height: 1.2;
  font-weight: $weight-medium;
  letter-spacing: 0.2px;
  color: $white;

  span {
    display: block;
    color: #9b9b9b;
  }
}

.speed-chart {
  position: relative;
  display: flex;
}

.line {
  fill: url(#linear);
}

.hover-circle {
  fill: $white;
  stroke: #3e3e3e;
  stroke-width: 2px;

  &.is-small {
    stroke-width: 1px;
  }
}

.mean-line,
.hover-line {
  stroke-width: 1px;
  stroke: #bcbcbc;
  pointer-events: none;
}

.mean-rect {
  position: absolute;
  display: inline-block;
  background-color: #484848;
  border-radius: 1px;
  padding: 0.0625rem 0.25rem;
  pointer-events: none;
}

.mean-text {
  color: $white;
  font-size: 0.625rem;
  line-height: 1.2;
  letter-spacing: 0.2px;
}

.overlay {
  fill: none;
  pointer-events: all;
}

.tooltip {
  opacity: 1;
  top: 1rem;
  left: 0.875rem;
  transition: none;

  .level {
    font-size: $size-small;
    line-height: 1.5;

    &:not(:last-child) {
      margin-bottom: 0.25rem;
    }

    .level-left {
      font-weight: $weight-medium;
      color: rgba($black, 0.87);

      & + .level-right {
        margin-left: 2.25rem;
      }
    }

    .level-right {
      color: rgba($black, 0.64);
    }
  }

  i {
    font-size: $size-small;
    margin-right: 0.5rem;
  }
}

.tooltip-inner {
  padding: 0.625rem 1rem;

  &::after {
    display: none;
  }
}
</style>
